import { captureMessage } from '@sentry/vue';
import UrlHelper from '@/services/UrlHelper';
import BadgingHelper from '@/services/BadgingHelper';
import CategoryAssets from '@/services/CategoryAssets';
import MixAndMatchHelper from '@/services/MixAndMatchHelper';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import { GetterTree } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import FacialFeaturesHelper from '@/services/FacialFeaturesHelper';
import TraitsHelper from '@/services/TraitsHelper';
import Vue from 'vue';

export interface OptionPreview {
  category: string;
  value: number;
  previewType: string;
  rotation: number | null;
}

interface MixAndMatchOptionPreview {
  category: string;
  values: Record<string, number>;
  isFullPiece: boolean;
}

const PREFIX_ONLY_CATEGORIES = [
  'ear',
  'eye_details',
  'face_lines',
  'eye',
  'hair',
  'mouth',
  'beard',
  'brow',
  'nose',
  'jaw',
  'cheek_details',
  'body',
  'breast',
  'hat',
  'glasses',
];

const getters: GetterTree<RootState, RootState> = {
  categories: (state) => {
    return state.categories;
  },
  mixAndMatchColourGroups: (state) => {
    return state.mixAndMatchColourGroups;
  },
  mixAndMatchMetadata: (state) => {
    return state.mixAndMatchMetadata;
  },
  facialFeaturesMetadata: (state) => {
    return state.facialFeaturesMetadata;
  },
  traits: (state) => {
    return state.selectedTraits;
  },
  isIOS12: (state) => {
    return state.isIOS12;
  },
  selectedColourOptionsForTraitItems: (state) => {
    return state.selectedColourOptionsForTraitItems;
  },
  selectedCategoryIndex: (state) => {
    return state.selectedCategoryIndex;
  },
  selectedCategory: (state, getters) => {
    return CategoryAssets.getFlatCategoryList(getters.gender, getters.style, state.flowMode)[
      state.selectedCategoryIndex
    ];
  },
  initialSectionId: (state) => {
    return state.initialSectionId;
  },
  centerActiveCategoryIcon: (state) => {
    return state.centerActiveCategoryIcon;
  },
  promotedCategories: (state) => {
    return [...state.initialPromotions.categories].filter(
      (category) => !state.stalePromotions.categories.has(category),
    );
  },
  isSectionPromoted: (state) => {
    return (category: string, sectionId: number) => {
      const findSectionPromoForCategory = (sectionPromo: SectionPromotion) =>
        BadgingHelper.isNestedPromoForCategory(category, sectionPromo) &&
        sectionPromo.sectionId === sectionId;
      const sectionIsPromoted =
        state.initialPromotions.sections.findIndex(findSectionPromoForCategory) !== -1;
      const notStale = state.stalePromotions.sections.findIndex(findSectionPromoForCategory) === -1;
      return sectionIsPromoted && notStale;
    };
  },
  isOptionPromoted: (state) => {
    return (category: string, optionId: number) => {
      const promotedOptionsForCategory = state.initialPromotions.options.find((optionPromo) =>
        BadgingHelper.isNestedPromoForCategory(category, optionPromo),
      );
      const staleOptionsForCategory = state.stalePromotions.options.find((optionPromo) =>
        BadgingHelper.isNestedPromoForCategory(category, optionPromo),
      );
      const optionIsPromoted =
        promotedOptionsForCategory && promotedOptionsForCategory.optionIds.includes(optionId);
      const notStale =
        !staleOptionsForCategory || !staleOptionsForCategory.optionIds.includes(optionId);
      return optionIsPromoted && notStale;
    };
  },
  mainPreview: (state, getters) => {
    if (isEmpty(state.selectedTraits)) {
      return '';
    }
    // Get the rotation of the avatar preview based on the category
    const categoryDetails = CategoryAssets.getCategoryDetails(getters.selectedCategory);

    return UrlHelper.getMainPreview(
      categoryDetails.rotation,
      state.selectedTraits,
      getters.selectedCategory,
    );
  },

  getOptionPreview: (state, getters) => {
    return ({ category, value, previewType, rotation }: OptionPreview) => {
      let traits = Object.assign({}, state.selectedTraits);

      // If the preview is for the body category, set the preview to the default outfit
      if (category === 'body') {
        delete traits.is_tucked;
        delete traits.outfit;
        const defaultOutfitTraits = MixAndMatchHelper.getDefaultOutfitTraits();
        Object.assign(traits, defaultOutfitTraits);

        // remove outfit keys if they are not vital
        DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.forEach((outfitKey) => {
          if (!DetailedOutfitHelper.VITAL_DETAILED_OUTFIT_KEYS.includes(outfitKey)) {
            delete traits[outfitKey];
          }
        });
      }

      if (DetailedOutfitHelper.EYE_TRAITS_KEYS.includes(category) && traits['glasses']) {
        delete traits.glasses;
      }

      const traitPrefixToKeep = PREFIX_ONLY_CATEGORIES.find((prefix) =>
        category.startsWith(prefix),
      );
      let cacheable = !!traitPrefixToKeep;

      // Only keep relevant option ids for previews with mannequin heads
      if (traitPrefixToKeep) {
        for (const key in traits) {
          if (key === 'gender' || key === 'style') {
            continue;
          }
          if (!key.startsWith(traitPrefixToKeep)) {
            delete traits[key];
          }
        }
      }

      // No need to remove existing colour keys if this preview is for the currently selected
      // option
      const isOptionPreviewForSelected = value === state.selectedTraits[category];
      if (TraitsHelper.isColourPickerTraitCategory(category) && !isOptionPreviewForSelected) {
        // Clean up category colour keys and only include those for the previewed item
        const colourKeyPrefix = TraitsHelper.getTraitColourKeyPrefix(category);
        Object.keys(traits).forEach((traitKey) => {
          if (traitKey.startsWith(colourKeyPrefix)) {
            delete traits[traitKey];
          }
        });

        // Apply selected colour option if valid for current item
        const colourOption = TraitsHelper.getSelectedColourPickerOptionOrDefaultForItem(
          category,
          value,
          getters.categories,
          getters.categoryMetadata.swatch_sets,
          getters.selectedColourOptionsForTraitItems,
        );
        if (colourOption) {
          const colourTraits = TraitsHelper.mapColourOptionToSelectedTraits(category, colourOption);
          Object.entries(colourTraits).forEach(([traitKey, colourVal]) => {
            traits[traitKey] = colourVal;
          });
        }
      }

      if (FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(category)) {
        traits = {
          style: 5,
          gender: state.initialTraits.gender,
        };
        const colour = FacialFeaturesHelper.getSavedColourIdOrDefault(
          category,
          String(value),
          state.facialFeatureTraits,
          state.facialFeaturesMetadata,
        );

        const newTraits = FacialFeaturesHelper.getFacialFeaturePreviewQueryParameters(
          state.facialFeaturesMetadata,
          category,
          String(value),
          colour,
        );

        Object.assign(traits, newTraits);
        cacheable = true;
      } else {
        traits[category] = value;
      }
      return UrlHelper.getOptionPreview(previewType, rotation, traits, cacheable);
    };
  },
  getMixAndMatchOptionPreview: (state) => {
    return ({ category, values, isFullPiece }: MixAndMatchOptionPreview) => {
      return UrlHelper.getMixAndMatchOptionPreview(
        category,
        values,
        state.initialTraits.gender,
        isFullPiece,
      );
    };
  },
  categoryList: (state) => {
    return CategoryAssets.getCategoryList(
      state.initialTraits.gender,
      state.initialTraits.style,
      state.flowMode,
    );
  },
  flowMode: (state) => {
    return state.flowMode;
  },
  exitDialogType: (state) => {
    return state.exitDialogType;
  },
  gender: (state) => {
    return state.initialTraits.gender;
  },
  style: (state) => {
    return state.initialTraits.style;
  },
  categoryMetadata: (state) => {
    return state.categoryMetadata;
  },
  scrollState: (state) => {
    return state.scrollState;
  },
  isFullBodyCategory: (state) => {
    return state.isFullBodyCategory;
  },
  selectedColourPalettesForMixAndMatchItems: (state) => {
    return state.selectedColourPalettesForMixAndMatchItems;
  },
  closetState: (state) => {
    return state.closetState;
  },
  closetItemsSixthBody: (state) => {
    return state.closetItemsSixthBody;
  },
  outfitSharingState: (state) => {
    return state.outfitSharingState;
  },
  topToggleTuckState: (state) => {
    return state.topToggleTuckState;
  },
  isTopToggleDisabled: (state) => {
    return state.isTopToggleDisabled;
  },
  avatarActions: (state) => {
    return state.avatarActions;
  },
  avatarActionIndex: (state) => {
    return state.avatarActionIndex;
  },
  previousUndoRedoAction: (state) => {
    return state.previousUndoRedoAction;
  },
  sixthBodyLegacyOutfits: (state) => {
    if (state.sixthBodyLegacyOutfits === null) {
      captureMessage('Sixth body supported legacy outfits is null');
    }
    return state.sixthBodyLegacyOutfits;
  },
  shouldShowDeleteSelfieDialog: (state) => {
    return state.shouldShowDeleteSelfieDialog;
  },
  shouldShowExitDialog: (state) => {
    return state.shouldShowExitDialog;
  },
  shouldShowOutfitDialog: (state) => {
    return state.shouldShowOutfitDialog;
  },
  shouldShowUnsupportedOSError: (state) => {
    return state.shouldShowUnsupportedOSError;
  },
  shouldShowItemUnavailableDialog: (state) => {
    return state.shouldShowItemUnavailableDialog;
  },
  backgroundId: (state) => {
    return state.backgroundId;
  },
  backgroundType: (state) => {
    return state.backgroundType;
  },
  backgroundUrl: (state) => {
    return state.backgroundUrl;
  },
  selectedBackgroundIds: (state) => {
    return state.selectedBackgroundIds;
  },
  isSelfiePreviewDismissed: (state) => {
    return state.isSelfiePreviewDismissed;
  },
  isSelfiePreviewExpanded: (state) => {
    return state.isSelfiePreviewExpanded;
  },
  disableUndoRedoButton: (state) => {
    return state.disableUndoRedoButton;
  },
  facialFeatureTraits: (state) => {
    return state.facialFeatureTraits;
  },
  facialFeatureSelectedGroupId: (state, getters) => {
    const category = getters.selectedCategory;
    if (!FacialFeaturesHelper.FACIAL_FEATURE_CATEGORY_KEYS.includes(category)) {
      return null;
    }

    return FacialFeaturesHelper.getSelectedGroupIdForCategory(
      category,
      getters.facialFeatureTraits,
    );
  },
  tuckStateForTop: (state) => {
    return state.tuckStateForTop;
  },
  is3dMode: (state) => {
    return state.is3dMode;
  },
  bottomWrapDown: (state) => {
    return state.bottomWrapDown;
  },
  builderIsFocused: (state) => {
    return state.builderIsFocused;
  },
};

export default getters;
